import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CITY_DISTRICTS } from '@/data/cityDistricts';
import { PRIMARY_ACTIVE_CITY } from '@/lib/geoScope';
import {
  DEBOUNCE_MIEJSCA_MS,
  czySzukac,
  tekstZapytania,
  usunZOstatnich,
  wczytajOstatnie,
  zapamietajMiejsce,
  zapiszOstatnie,
  type PodpowiedzMiejsca,
  type StatusPodpowiedzi,
} from '@/lib/bliskoDo';
import { KLUCZ_MOJEGO_WYSZUKIWANIA, saveRecentPlaces, type MojeWyszukiwanie } from '@/hooks/useMojeWyszukiwanie';
import type { RecentPlace } from '@/types/savedSearch';

export interface WynikPodpowiedzi {
  places: PodpowiedzMiejsca[];
  status: StatusPodpowiedzi;
}
export type Szukaj = (q: string) => Promise<WynikPodpowiedzi>;

/**
 * Jedno wywołanie `place-autocomplete` (klucz LocationIQ zostaje na serwerze). Środek miasta z
 * `CITY_DISTRICTS` (Konwencja 23: nie wpisujemy miasta). Awaria = „niedostępne”, bez ponawiania.
 */
export const szukajMiejsc: Szukaj = async (q) => {
  const centrum = CITY_DISTRICTS[PRIMARY_ACTIVE_CITY]?.center;
  if (!centrum) return { places: [], status: 'unavailable' };
  try {
    const { data, error } = await supabase.functions.invoke<WynikPodpowiedzi>('place-autocomplete', { body: { q, center: centrum } });
    if (error || !data || !Array.isArray(data.places)) return { places: [], status: 'unavailable' };
    return data;
  } catch {
    return { places: [], status: 'unavailable' };
  }
};

/**
 * Podpowiedzi „Blisko do…”: od 3 znaków, ~300 ms po ostatnim naciśnięciu klawisza, jedno zapytanie
 * na jedną przerwę w pisaniu. Odpowiedź na stary tekst (szybkie pisanie) jest ignorowana.
 * Chwilowy limit LocationIQ (2/s) zostawia poprzednie wyniki zamiast migać komunikatem.
 */
export function usePodpowiedziMiejsc(query: string, szukaj: Szukaj = szukajMiejsc) {
  const [wynik, setWynik] = useState<WynikPodpowiedzi>({ places: [], status: 'ok' });
  const [laduje, setLaduje] = useState(false);
  const q = tekstZapytania(query);

  useEffect(() => {
    if (!czySzukac(q)) {
      setWynik({ places: [], status: 'ok' });
      setLaduje(false);
      return;
    }
    let anulowane = false;
    setLaduje(true);
    const timer = setTimeout(async () => {
      const w = await szukaj(q);
      if (anulowane) return;
      setWynik((poprzedni) => (w.status === 'limited' && poprzedni.places.length ? poprzedni : w));
      setLaduje(false);
    }, DEBOUNCE_MIEJSCA_MS);
    return () => {
      anulowane = true;
      clearTimeout(timer);
    };
  }, [q, szukaj]);

  return { ...wynik, laduje };
}

const przegladarka = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

/**
 * „Ostatnio wybrane” (§1.70): zalogowany najemca — `recent_places` na koncie (przez
 * `save_my_filters`, F1); każdy inny (gość, właściciel) — localStorage. Lista żyje osobno od
 * filtrów, więc „Wyczyść” jej nie dotyka.
 */
export function useOstatnieMiejsca() {
  const qc = useQueryClient();
  // Tylko obserwator wpisu, który już czyta strona wyników (`useMojeWyszukiwanie`): bez
  // własnego zapytania, żeby otwarcie panelu nie odświeżało zbioru, z którym porównuje „Zapisz”.
  const { data: moje } = useQuery<MojeWyszukiwanie>({ queryKey: KLUCZ_MOJEGO_WYSZUKIWANIA, enabled: false });
  const naKoncie = moje?.isTenant ?? false;
  const [lokalne, setLokalne] = useState<RecentPlace[]>(() => wczytajOstatnie(przegladarka()));
  const lista = naKoncie ? (moje?.recentPlaces ?? []) : lokalne;

  const ustaw = useCallback(
    (nowa: RecentPlace[]) => {
      if (naKoncie) {
        // Od razu na ekranie; zapis w tle. Nieudany zapis cofa listę do stanu z bazy.
        qc.setQueryData<MojeWyszukiwanie>(KLUCZ_MOJEGO_WYSZUKIWANIA, (m) => (m ? { ...m, recentPlaces: nowa } : m));
        saveRecentPlaces(nowa).catch(() => qc.invalidateQueries({ queryKey: KLUCZ_MOJEGO_WYSZUKIWANIA }));
      } else {
        setLokalne(nowa);
        zapiszOstatnie(przegladarka(), nowa);
      }
    },
    [naKoncie, qc],
  );

  // Gość: listę pisze kilka komponentów naraz (panel „Blisko do…” i — od §1.90 — pole obszaru, które
  // jest zamontowane cały czas), więc punktem wyjścia jest to, co JEST w przeglądarce, a nie stan
  // z chwili montowania; inaczej wybór w polu nadpisałby miejsce wybrane przed chwilą w panelu.
  // Pusty odczyt (także przy zablokowanym localStorage) — zostaje lista z pamięci komponentu.
  const aktualna = () => {
    if (naKoncie) return lista;
    const zapisane = wczytajOstatnie(przegladarka());
    return zapisane.length ? zapisane : lokalne;
  };

  return {
    lista,
    zapamietaj: (p: RecentPlace) => ustaw(zapamietajMiejsce(aktualna(), p)),
    usun: (p: RecentPlace) => ustaw(usunZOstatnich(aktualna(), p)),
  };
}
